import { Box, Flex, IconFont, singleTextEllipsis, Tooltip } from '@byecode/ui'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { PERSON_ID, PERSON_MAP_ICON, PERSON_MAP_ICON_COLOR } from '../../constants'
import { useApplicationContext } from '../../contexts'
import type { UserOption } from '../../types'
import { getColorById, getImageFullUrlInApplication } from '../../utils'

interface TagItemPreviewerProps {
    data: UserOption
    clearable?: boolean
    isHiddenUserId?: boolean
    disabled?: boolean
    onRemove?: (userId: string) => void
}

const SCxAvatar = styled.img`
    width: 22px;
    min-width: 22px;
    height: 22px;
    margin-right: 4px;
    border-radius: 50%;
`

const SCxIcon = styled(IconFont)`
    margin-right: 4px;
`

const SCxAvatarText = styled.div<{ background?: string }>`
    display: flex;
    justify-content: center;
    width: 22px;
    min-width: 22px;
    height: 22px;
    align-items: center;
    font-size: var(--font-size-sm);
    margin-right: 4px;
    border-radius: 50%;
    overflow: hidden;
    color: var(--color-white);
    background-color: ${({ background }) => background || 'var(--color-gray-2)'};
`

const SCxText = styled.div`
    height: 100%;
    line-height: 22px;
    white-space: nowrap;
    ${singleTextEllipsis}
`

const SCxClearIcon = styled(IconFont)`
    margin-left: 4px;
`

const SCxCellValue = styled.div`
    max-width: 100%;
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-right: 6px;
    overflow: hidden;

    ${SCxClearIcon} {
        opacity: 0.5;
    }
    :hover {
        ${SCxClearIcon} {
            opacity: 1;
        }
    }
`

const SCxPersonIcon = styled.div`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 4px;
    border: 1px solid var(--color-gray-200);
    background: var(--color-gray-50);
`

export const TagItemPreviewer: React.FC<TagItemPreviewerProps> = ({ data, clearable, isHiddenUserId, disabled, onRemove }) => {
    const { id, name, avatar } = data
    const background = getColorById(id)
    const { appId } = useApplicationContext()
    const avatarContent = useMemo(() => {
        if (PERSON_ID.has(id)) {
            return (
                <SCxPersonIcon>
                    <IconFont type={PERSON_MAP_ICON[id]} fill={PERSON_MAP_ICON_COLOR[id]} size={16} />
                </SCxPersonIcon>
            )
        }
        if (id === '{currentUserId}') {
            return <SCxIcon size={22} type="UserCircle" color="var(--color-gray-400)" />
        }
        if (avatar) {
            return <SCxAvatar src={getImageFullUrlInApplication(appId, avatar)} />
        }
        return <SCxAvatarText background={background}>{name.charAt(0)}</SCxAvatarText>
    }, [appId, avatar, background, id, name])

    return (
        <Tooltip title={name} key={id}>
            <SCxCellValue data-field-other data-field-disabled={!!disabled}>
                {avatarContent}
                <Box>
                    <SCxText>{name}</SCxText>
                    {!isHiddenUserId && id !== '{currentUserId}' && <SCxText>{id}</SCxText>}
                </Box>
                {clearable && (
                    <Flex alignItems="center" data-field-other-icon>
                        <SCxClearIcon
                            onClick={event => {
                                event.stopPropagation()
                                onRemove?.(id)
                            }}
                            type="Close"
                        />
                    </Flex>
                )}
            </SCxCellValue>
        </Tooltip>
    )
}
